import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  
  CircularProgress,
  Grid,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  
  Tooltip,
  Paper,
  Divider,
} from "@mui/material";
import {
  ExpandMore,
  ArrowBack,
  Home as HomeIcon,
  Info,
  ChevronRight,
} from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { userService } from "../../services/userService";

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const profileId = new URLSearchParams(location.search).get("profile_id") || "";
  const [userData, setUserData] = useState(null);
  const [mainUserName, setMainUserName] = useState(location.state?.mainUserName || "");
  const [loading, setLoading] = useState(true);
  const [selectedSubUser, setSelectedSubUser] = useState("");
  const [subUsers, setSubUsers] = useState([]);
  const [expandedPanel, setExpandedPanel] = useState("profile_summary");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedChart, setSelectedChart] = useState("indu_lagna_chart");
  const [chartImage, setChartImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [chatSearch, setChatSearch] = useState("");
  const filteredConversations = (userData?.conversations || []).filter((c) =>
    (c.title || "").toLowerCase().includes(chatSearch.toLowerCase())
  );
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatModalLoading, setChatModalLoading] = useState(false);
  const [chatModalData, setChatModalData] = useState(null);
  const [chatModalError, setChatModalError] = useState("");

  const D_CHART_IDS = ["d1","d2","d3","d4","d5","d6","d7","d8","d9","d10","d11","d12","d16","d20","d24","d27","d30","d40","d45","d60"];

  useEffect(() => {
    loadUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, profileId]);

  useEffect(() => {
    loadChartImage(selectedChart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChart, userId, profileId]);

  useEffect(() => {
    if (Array.isArray(location.state?.subUsers)) {
      setSubUsers(location.state.subUsers);
    }
  }, [location.state]);

  useEffect(() => {
    setSelectedSubUser(profileId || "");
  }, [profileId]);

  const loadUserDetails = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const res = await userService.getUserDetails(userId, profileId);
      if (res.success) {
        const data = res.data || {};
        // keep main user's name from API if available (even when profile is incomplete)
        setMainUserName(data?.name || location.state?.mainUserName || "");

        // Check if user has chart data (which indicates they've completed onboarding)
        const hasChartData = Boolean(
          data.charts || 
          data.planet_positions || 
          data.indu_lagna_chart || 
          data.karakamsha_lagna_chart || 
          data.arudha_lagna_chart
        );

        if (!hasChartData) {
          // Show a friendly, non-technical message when profile is incomplete
          setUserData(null);
          setErrorMessage(
            "User profile is incomplete. Details will be available once the user completes onboarding."
          );
        } else {
          const allCharts = {
            ...(data?.charts || {}),
            ...(data?.indu_lagna_chart ? { indu_lagna_chart: data.indu_lagna_chart } : {})
          };
          setUserData({
            userId: userId,
            userName: data?.name || "User",
            charts: allCharts,
            conversations: data?.conversations || [],
            reports: data?.reports || [],
            compatibility_reports: data?.compatibility_reports || [],
            indu_lagna_chart: data?.indu_lagna_chart || null,
            planet_positions: data?.planet_positions || null,
            karakamsha_lagna_chart: data?.karakamsha_lagna_chart || null,
            arudha_lagna_chart: data?.arudha_lagna_chart || null,
            profile_summary: data?.profile_summary || null,
          });
          setErrorMessage("");
        }
      } else {
        console.error("Failed to fetch user details:", res.message);
        setUserData(null);
        setErrorMessage("Unable to fetch user details. Please try again later.");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setUserData(null);
      setErrorMessage("Unable to fetch user details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const loadChartImage = async (chartId) => {
    if (chartId === "indu_lagna_chart" || chartId === "planet_positions" || chartId === "karakamsha_lagna_chart" || chartId === "arudha_lagna_chart" || chartId === "d6" || chartId === "d11") {
      setChartImage(null);
      setImageLoading(false);
      return;
    }
    if (!userId || !D_CHART_IDS.includes(chartId)) return;
    setImageLoading(true);
    try {
      const res = await userService.getChartImage(userId, chartId, profileId);
      if (res.success) {
        setChartImage(res.data);
      } else {
        console.error("Failed to fetch chart image:", res.message);
        setChartImage(null);
      }
    } catch (err) {
      console.error("Error fetching chart image:", err);
      setChartImage(null);
    } finally {
      setImageLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
    if (isExpanded) {
      const target = event.currentTarget;
      setTimeout(() => {
        target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 350);
    }
  };

  const handleOpenChat = async (chatId) => {
    setChatModalOpen(true);
    setChatModalLoading(true);
    setChatModalError("");
    setChatModalData(null);
    try {
      const res = await userService.getChatHistory(userId, chatId);
      if (res.success) {
        setChatModalData(res.data || null);
      } else {
        setChatModalError(res.message || 'Failed to load chat history');
      }
    } catch (err) {
      console.error(err);
      setChatModalError('Failed to load chat history.');
    } finally {
      setChatModalLoading(false);
    }
  };

  const handleCloseChat = () => {
    setChatModalOpen(false);
    setChatModalData(null);
    setChatModalError("");
  };

  const handleOpenReport = (fileUrl) => {
    if (fileUrl) {
      // If the URL is relative, prepend the base URL
      let fullUrl = fileUrl;
      if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://13.60.236.18';
        fullUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      }
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } else {
      // If no file URL, show a message to the user
      alert('Report not generated yet. It will be available once the user views or interacts with it.');
    }
  };

  const handleBack = () => {
    // Navigate back to users list preserving any query params that were present
    // If this component is mounted at a route that includes the search params
    // in the URL (e.g. /users/:id?search=...), use them. Otherwise fall back to plain /users
    const qs = location.search || "";
    navigate(`/users${qs}`);
  };

  const handleChartChange = (event, newValue) => {
    setSelectedChart(newValue);
    loadChartImage(newValue);
  };

  const handleSubUserChange = (event) => {
    const nextProfileId = event.target.value || "";
    const params = new URLSearchParams(location.search);
    if (nextProfileId) {
      params.set("profile_id", nextProfileId);
    } else {
      params.delete("profile_id");
    }
    const qs = params.toString();
    navigate(`/users/${userId}${qs ? `?${qs}` : ""}`, {
      state: { subUsers: subUsers || [], mainUserName: mainUserName || userData?.userName || location.state?.mainUserName }
    });
  };

  const getSelectedSubUserLabel = () => {
    const mainName = mainUserName || userData?.userName || location.state?.mainUserName;
    if (!selectedSubUser) {
      return mainName || "Main User";
    }
    const match = subUsers.find((subUser) => subUser.id === selectedSubUser);
    return match?.name || (mainName || "Selected User");
  };

  const getChartName = (chartKey) => {
    const chartNames = {
      d1: "D1 - Rashi (Birth Chart)",
      d2: "D2 - Hora (Wealth)",
      d3: "D3 - Drekkana (Siblings)",
      d4: "D4 - Chaturthamsa (Fortune)",
      d5: "D5 - Panchamsa (Fame)",
      d6: "D6 - Shashthamsa (Health)",
      d7: "D7 - Saptamsa (Children)",
      d8: "D8 - Ashtamsa (Longevity)",
      d9: "D9 - Navamsa (Marriage)",
      d10: "D10 - Dasamsa (Career)",
      d11: "D11 - Rudramsa (Destruction)",
      d12: "D12 - Dwadasamsa (Parents)",
      d16: "D16 - Shodasamsa (Vehicles)",
      d20: "D20 - Vimsamsa (Spiritual)",
      d24: "D24 - Chaturvimsamsa (Education)",
      d27: "D27 - Nakshatramsa (Strengths)",
      d30: "D30 - Trimsamsa (Evils)",
      d40: "D40 - Khavedamsa (Auspicious)",
      d45: "D45 - Akshavedamsa (General)",
      d60: "D60 - Shashtiamsa (Karma)",
      indu_lagna_chart: "Indu Lagna (Wealth & Prosperity)",
      planet_positions: "Planet Positions",
      karakamsha_lagna_chart: "Karakamsha Lagna (Career)",
      arudha_lagna_chart: "Arudha Lagna (Image & Perception)",
    };
    return chartNames[chartKey] || chartKey.toUpperCase();
  };

  const renderPlanetPositions = (planetData) => {
    if (!planetData || typeof planetData !== "object") {
      return (
        <Typography sx={{ color: "#666666", fontSize: "14px" }}>
          No planet position data available.
        </Typography>
      );
    }

    const getField = (obj, candidates) => {
      if (!obj || typeof obj !== "object") return "";
      const keys = Object.keys(obj || {});
      for (const cand of candidates) {
        if (obj[cand] !== undefined && obj[cand] !== null) return obj[cand];
        const found = keys.find((k) => k.toLowerCase() === cand.toLowerCase());
        if (found) return obj[found];
      }
      return "";
    };

    const toDMS = (val) => {
      const n = Number(val);
      if (isNaN(n)) return val || "";
      const deg = Math.floor(n);
      const mFloat = (n - deg) * 60;
      const min = Math.floor(mFloat);
      const sec = Math.round((mFloat - min) * 60);
      const p2 = (x) => String(x).padStart(2, "0");
      return `${String(deg).padStart(3, "0")}°${p2(min)}'${p2(sec)}"`;
    };

    const rows = Object.entries(planetData);

    return (
      <Box>
        <Typography sx={{ color: "#333333", fontSize: "18px", fontWeight: 600, marginBottom: 2 }}>
          Planetary Positions
        </Typography>

        <TableContainer component={Paper} sx={{ backgroundColor: "#FFF9E6", borderRadius: "8px", border: "1px solid #E6C58A" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Planet</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Name</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Degree (DMS)</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Normal Degree</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Normal Speed</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Sign</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Sign Lord</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Nakshatra</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Nakshatra Lord</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Nakshatra Pad</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>House</strong></TableCell>
                <TableCell sx={{ color: "#000000", fontWeight: 700 }}><strong>Planet Awastha</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(([planetName, planetInfo]) => {
                const name = getField(planetInfo, ["name", "NAME"]);
                const degRaw = getField(planetInfo, ["degree", "normdegree", "norm_degree", "full_degree", "fulldegree"]);
                const degree = toDMS(degRaw);
                const normDegreeRaw = getField(planetInfo, ["normdegree", "norm_degree", "normDegree", "norm_degree"]);
                const normDegree = !isNaN(Number(normDegreeRaw)) ? Number(normDegreeRaw).toFixed(2) : normDegreeRaw;
                const speedRaw = getField(planetInfo, ["speed", "SPEED"]);
                const speed = !isNaN(Number(speedRaw)) ? Number(speedRaw).toFixed(2) : speedRaw;
                const sign = getField(planetInfo, ["sign", "SIGN", "sign_name"]);
                const signLord = getField(planetInfo, ["signlord", "sign_lord", "SIGNLORD"]);
                const nakshatra = getField(planetInfo, ["nakshatra", "NAKSHATRA"]);
                const nakshatraLord = getField(planetInfo, ["nakshatralord", "nakshatra_lord", "NAKSHATRALORD"]);
                const nakshatraPadRaw = getField(planetInfo, ["nakshatra_pad", "nakshatra pad", "nakshatraPad", "nakshatra_pad"]);
                const nakshatraPad = !isNaN(Number(nakshatraPadRaw)) ? Number(nakshatraPadRaw).toFixed(2) : nakshatraPadRaw;
                const house = getField(planetInfo, ["house", "HOUSE"]);
                const planetAwastha = getField(planetInfo, ["planet_awastha", "planet awastha", "planet_awastha"]);

                return (
                  <TableRow key={planetName}>
                    <TableCell sx={{ width: 120 }}>
                      <Typography sx={{ color: "#E1AD01", fontWeight: 700 }}>{planetName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333", fontWeight: 600 }}>{degree}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{normDegree}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{speed}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{sign}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{signLord}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{nakshatra}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{nakshatraLord}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{nakshatraPad}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{house}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#333333" }}>{planetAwastha}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderKaramshaLagnaChart = (karamshaData) => {
    if (!karamshaData || typeof karamshaData !== "object") {
      return (
        <Typography sx={{ color: "#666666", fontSize: "14px" }}>
          No Karamsha Lagna chart data available.
        </Typography>
      );
    }

    return (
      <Box>
        {/* Karamsha Lagna Info */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #E1AD01 0%, #DAB96E 100%)",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={{ color: "#FFFFFF", fontSize: "14px", opacity: 0.9, marginBottom: 0.5 }}>
              Karakamsha Lagna
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontSize: "28px", fontWeight: 700 }}>
              {karamshaData.karakamsha_lagna || karamshaData.karamsha_lagna || karamshaData.ascendant || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ color: "#FFFFFF", fontSize: "14px", opacity: 0.9, marginBottom: 0.5 }}>
              Chart Type
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600 }}>
              Karakamsha Lagna (D9 Career Focus)
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ marginY: 3, backgroundColor: "#E0E0E0" }} />

        {/* Houses & Planets Details */}
        <Typography
          sx={{
            color: "#333333",
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: 3,
          }}
        >
          Houses & Planetary Positions
        </Typography>
        
        <Grid container spacing={2}>
          {karamshaData.houses?.map((house) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={house.house_number}>
              <Tooltip 
                title={`${house.sign_name} - House ${house.house_number}${house.planet.length > 0 ? ` | ${house.planet.length} planet(s)` : ''}`}
                arrow
                placement="top"
              >
                <Card
                  sx={{
                    backgroundColor:
                      house.planet.length > 0 ? "#FFF9E6" : "#F9F9F9",
                    border:
                      house.planet.length > 0
                        ? "2px solid #E1AD01"
                        : "1px solid #E0E0E0",
                    borderRadius: "12px",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: house.planet.length > 0 
                        ? "0 6px 20px rgba(204, 158, 73, 0.3)"
                        : "0 4px 12px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginRight: 1 }}>
                      <HomeIcon sx={{ color: "#E1AD01", fontSize: "16px" }} />
                      <Typography
                        sx={{
                          color: "#E1AD01",
                          fontSize: "14px",
                          fontWeight: 700,
                        }}
                      >
                        House {house.house_number}
                      </Typography>
                    </Box>
                    {house.planet.length > 0 && (
                      <Chip
                        label={`${house.planet.length} ${house.planet.length === 1 ? 'Planet' : 'Planets'}`}
                        size="small"
                        sx={{
                          backgroundColor: "#E1AD01",
                          color: "#FFFFFF",
                          fontSize: "10px",
                          fontWeight: 600,
                          height: "22px",
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    sx={{
                      color: "#333333",
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: 2,
                    }}
                  >
                   Sign - {house.sign_name}
                  </Typography>

                  {house.planet.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {house.planet.map((planet, index) => (
                        <Tooltip key={index} title={`${planet} in ${house.sign_name}`} arrow>
                          <Chip
                            label={planet}
                            size="small"
                            sx={{
                              backgroundColor: "#333333",
                              color: "#FFFFFF",
                              fontSize: "11px",
                              fontWeight: 500,
                              height: "24px",
                              "&:hover": {
                                backgroundColor: "#E1AD01",
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.6 }}>
                      <Info sx={{ fontSize: "14px", color: "#999999" }} />
                      <Typography
                        sx={{
                          color: "#999999",
                          fontSize: "12px",
                          fontStyle: "italic",
                        }}
                      >
                        No planets
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderArudhaLagnaChart = (arudhaData) => {
    if (!arudhaData || typeof arudhaData !== "object") {
      return (
        <Typography sx={{ color: "#666666", fontSize: "14px" }}>
          No Arudha Lagna chart data available.
        </Typography>
      );
    }

    return (
      <Box>
        {/* Arudha Lagna Info */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #E1AD01 0%, #DAB96E 100%)",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={{ color: "#FFFFFF", fontSize: "14px", opacity: 0.9, marginBottom: 0.5 }}>
              Arudha Lagna
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontSize: "28px", fontWeight: 700 }}>
              {arudhaData.arudha_lagna || arudhaData.ascendant || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ color: "#FFFFFF", fontSize: "14px", opacity: 0.9, marginBottom: 0.5 }}>
              Chart Type
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600 }}>
              Arudha Lagna (Image & Perception)
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ marginY: 3, backgroundColor: "#E0E0E0" }} />

        {/* Houses & Planets Details */}
        <Typography
          sx={{
            color: "#333333",
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: 3,
          }}
        >
          Houses & Planetary Positions
        </Typography>
        
        <Grid container spacing={2}>
          {arudhaData.houses?.map((house) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={house.house_number}>
              <Tooltip 
                title={`${house.sign_name} - House ${house.house_number}${house.planet.length > 0 ? ` | ${house.planet.length} planet(s)` : ''}`}
                arrow
                placement="top"
              >
                <Card
                  sx={{
                    backgroundColor:
                      house.planet.length > 0 ? "#FFF9E6" : "#F9F9F9",
                    border:
                      house.planet.length > 0
                        ? "2px solid #E1AD01"
                        : "1px solid #E0E0E0",
                    borderRadius: "12px",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: house.planet.length > 0 
                        ? "0 6px 20px rgba(204, 158, 73, 0.3)"
                        : "0 4px 12px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginRight: 1 }}>
                      <HomeIcon sx={{ color: "#E1AD01", fontSize: "16px" }} />
                      <Typography
                        sx={{
                          color: "#E1AD01",
                          fontSize: "14px",
                          fontWeight: 700,
                        }}
                      >
                        House {house.house_number}
                      </Typography>
                    </Box>
                    {house.planet.length > 0 && (
                      <Chip
                        label={`${house.planet.length} ${house.planet.length === 1 ? 'Planet' : 'Planets'}`}
                        size="small"
                        sx={{
                          backgroundColor: "#E1AD01",
                          color: "#FFFFFF",
                          fontSize: "10px",
                          fontWeight: 600,
                          height: "22px",
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    sx={{
                      color: "#333333",
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: 2,
                    }}
                  >
                   Sign - {house.sign_name}
                  </Typography>

                  {house.planet.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {house.planet.map((planet, index) => (
                        <Tooltip key={index} title={`${planet} in ${house.sign_name}`} arrow>
                          <Chip
                            label={planet}
                            size="small"
                            sx={{
                              backgroundColor: "#333333",
                              color: "#FFFFFF",
                              fontSize: "11px",
                              fontWeight: 500,
                              height: "24px",
                              "&:hover": {
                                backgroundColor: "#E1AD01",
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.6 }}>
                      <Info sx={{ fontSize: "14px", color: "#999999" }} />
                      <Typography
                        sx={{
                          color: "#999999",
                          fontSize: "12px",
                          fontStyle: "italic",
                        }}
                      >
                        No planets
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderChartData = (chartData) => {
    if (!chartData || typeof chartData !== "object") {
      return (
        <Typography sx={{ color: "#666666", fontSize: "14px" }}>
          No chart data available.
        </Typography>
      );
    }

    // Handle special tabs
    if (selectedChart === "planet_positions") {
      return renderPlanetPositions(userData?.planet_positions);
    }
    if (selectedChart === "karakamsha_lagna_chart") {
      return renderKaramshaLagnaChart(userData?.karakamsha_lagna_chart);
    }
    if (selectedChart === "arudha_lagna_chart") {
      return renderArudhaLagnaChart(userData?.arudha_lagna_chart);
    }

    const chart = chartData[selectedChart];
    if (!chart) {
      return (
        <Typography sx={{ color: "#666666", fontSize: "14px" }}>
          Selected chart not available.
        </Typography>
      );
    }

    return (
      <Box>
        {/* Ascendant Info */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #E1AD01 0%, #DAB96E 100%)",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={{ color: "#FFFFFF", fontSize: "14px", opacity: 0.9, marginBottom: 0.5 }}>
              {selectedChart === "indu_lagna_chart" ? "Indu Lagna" : "Ascendant (Lagna)"}
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontSize: "28px", fontWeight: 700 }}>
              {chart.ascendant || chart.indu_lagna || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ color: "#FFFFFF", fontSize: "14px", opacity: 0.9, marginBottom: 0.5 }}>
              Chart Type
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600 }}>
              {getChartName(selectedChart)}
            </Typography>
          </Box>
        </Box>

        {/* Chart Image Section - Only show for D charts, not for indu_lagna_chart, planet_positions, karakamsha_lagna_chart, arudha_lagna_chart, d6, or d11 */}
        {selectedChart !== "indu_lagna_chart" && selectedChart !== "planet_positions" && selectedChart !== "karakamsha_lagna_chart" && selectedChart !== "arudha_lagna_chart" && selectedChart !== "d6" && selectedChart !== "d11" && (
          <Box sx={{ marginBottom: 3 }}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: "#F9F9F9",
                borderRadius: "12px",
                padding: "24px",
                border: "1px solid #E0E0E0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                position: "relative",
              }}
            >
              {imageLoading ? (
                <CircularProgress sx={{ color: "#E1AD01" }} />
              ) : chartImage ? (
                <Box
                  component="img"
                  src={chartImage}
                  alt={`${getChartName(selectedChart)} Chart`}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "600px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : (
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                    Chart image not available
                  </Typography>
                </Box>
              )}
              {chartImage && (
                <Typography
                  sx={{
                    display: 'none',
                    color: "#666666",
                    fontSize: "14px",
                    textAlign: "center"
                  }}
                >
                  Failed to load chart image
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        <Divider sx={{ marginY: 3, backgroundColor: "#E0E0E0" }} />

        {/* Houses & Planets Details */}
        <Typography
          sx={{
            color: "#333333",
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: 3,
          }}
        >
          Houses & Planetary Positions
        </Typography>
        
        <Grid container spacing={2}>
          {chart.houses?.map((house) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={house.house_number}>
              <Tooltip 
                title={`${house.sign_name} - House ${house.house_number}${house.planet.length > 0 ? ` | ${house.planet.length} planet(s)` : ''}`}
                arrow
                placement="top"
              >
                <Card
                  sx={{
                    backgroundColor:
                      house.planet.length > 0 ? "#FFF9E6" : "#F9F9F9",
                    border:
                      house.planet.length > 0
                        ? "2px solid #E1AD01"
                        : "1px solid #E0E0E0",
                    borderRadius: "12px",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: house.planet.length > 0 
                        ? "0 6px 20px rgba(204, 158, 73, 0.3)"
                        : "0 4px 12px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                <CardContent>
                  {/* House Number */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginRight: 1 }}>
                      <HomeIcon sx={{ color: "#E1AD01", fontSize: "16px" }} />
                      <Typography
                        sx={{
                          color: "#E1AD01",
                          fontSize: "14px",
                          fontWeight: 700,
                        }}
                      >
                        House {house.house_number}
                      </Typography>
                    </Box>
                    {house.planet.length > 0 && (
                      <Chip
                        label={`${house.planet.length} ${house.planet.length === 1 ? 'Planet' : 'Planets'}`}
                        size="small"
                        sx={{
                          backgroundColor: "#E1AD01",
                          color: "#FFFFFF",
                          fontSize: "10px",
                          fontWeight: 600,
                          height: "22px",
                        }}
                      />
                    )}
                  </Box>

                  {/* Sign Name */}
                  <Typography
                    sx={{
                      color: "#333333",
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: 2,
                    }}
                  >
                   Sign - {house.sign_name}
                  </Typography>

                  {/* Planets */}
                  {house.planet.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {house.planet.map((planet, index) => (
                        <Tooltip key={index} title={`${planet} in ${house.sign_name}`} arrow>
                          <Chip
                            label={planet}
                            size="small"
                            sx={{
                              backgroundColor: "#333333",
                              color: "#FFFFFF",
                              fontSize: "11px",
                              fontWeight: 500,
                              height: "24px",
                              "&:hover": {
                                backgroundColor: "#E1AD01",
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.6 }}>
                      <Info sx={{ fontSize: "14px", color: "#999999" }} />
                      <Typography
                        sx={{
                          color: "#999999",
                          fontSize: "12px",
                          fontStyle: "italic",
                        }}
                      >
                        No planets
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#000000",
        }}
      >
        <CircularProgress sx={{ color: "#E1AD01" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: "24px",
        backgroundColor: "#000000",
        minHeight: "100vh",
      }}
    >
      {/* Header with Back Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <IconButton
          onClick={handleBack}
          sx={{
            color: "#E1AD01",
            marginRight: 2,
            "&:hover": {
              backgroundColor: "rgba(204, 158, 73, 0.1)",
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: "32px",
            fontWeight: 600,
          }}
        >
          User Details
        </Typography>
      </Box>

      {/* User Name */}
      <Box>
        {errorMessage ? (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                marginBottom: 2,
                backgroundColor: "transparent",
                color: "#FFB74D",
                borderColor: "#E1AD01"
              }}
            >
              {errorMessage}
            </Alert>
          ) : (
          <>
            <Box sx={{ marginBottom: 4 }}>
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontSize: "24px",
                  fontWeight: 500,
                }}
              >
                {(() => {
                  const mainName = mainUserName || userData?.userName || location.state?.mainUserName;
                  return selectedSubUser
                    ? (subUsers.find(s => s.id === selectedSubUser)?.name || mainName)
                    : (mainName || "User");
                })()}
              </Typography>
            </Box>

            {/* Subtitle + Select under subtitle */}
            {subUsers.length > 0 && (
              <Box sx={{ marginBottom: 3 }}>
                <Select
                  value={selectedSubUser}
                  onChange={handleSubUserChange}
                  displayEmpty
                  renderValue={() => getSelectedSubUserLabel()}
                  sx={{
                    backgroundColor: "#FFFFFF",
                    minWidth: 260,
                    borderRadius: "25px",
                    padding: "6px 14px",
                    fontSize: "14px",
                    color: "#333333",
                    '& .MuiSelect-select': {
                      color: '#333333',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 6px',
                      paddingRight: '36px'
                    },
                    '& .MuiSelect-icon': {
                      color: '#333333',
                      right: 12,
                      display: 'block'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#FFFFFF',
                        color: '#333333',
                        boxShadow: '0px 8px 30px rgba(0,0,0,0.2)',
                        borderRadius: '12px',
                        zIndex: 1400,
                        padding: 0
                      }
                    },
                    MenuListProps: {
                      sx: {
                        paddingY: 0
                      }
                    },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' }
                  }}
                >
                  <MenuItem sx={{ color: '#666666' }} value="">
                    {mainUserName || userData?.userName || location.state?.mainUserName || "Main User"}
                  </MenuItem>
                  {subUsers.map((subUser) => (
                    <MenuItem key={subUser.id || subUser.name} sx={{ color: '#333333' }} value={subUser.id}>
                      {subUser.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {/* Profile Summary Section */}
            {userData.profile_summary && (
              <Accordion
                expanded={expandedPanel === "profile_summary"}
                onChange={handleAccordionChange("profile_summary")}
                sx={{
                  backgroundColor: "#1A1A1A",
                  borderRadius: "16px !important",
                  marginBottom: 2,
                  border: "1px solid #333333",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: "#E1AD01" }} />}
                  sx={{
                    padding: "16px 24px",
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#FFFFFF",
                      fontSize: "20px",
                      fontWeight: 600,
                    }}
                  >
                    Profile Summary
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "24px",
                    backgroundColor: "#F5F5F5",
                    borderRadius: "0 0 16px 16px",
                  }}
                >
                  {(() => {
                    const summary = userData.profile_summary;
                    
                    // Better parsing logic
                    const lines = summary.split('\n');
                    const userName = lines.find(l => l.startsWith('User Name:'))?.split(':')[1]?.trim() || 'N/A';
                    const userId = lines.find(l => l.startsWith('User ID:'))?.split(':')[1]?.trim() || 'N/A';
                    
                    // Group lines into sections based on patterns
                    const sections = [];
                    let currentSection = null;
                    
                    for (let i = 0; i < lines.length; i++) {
                      const line = lines[i].trim();
                      
                      // Skip User Name and User ID as they're in header
                      if (line.startsWith('User Name:') || line.startsWith('User ID:')) continue;
                      if (!line) continue;
                      
                      // Check if this is a section header (no colon, and looks like a title)
                      const isHeader = !line.includes(':') && line.length > 3 && 
                                      (line.includes('Details') || line.includes('Facts') || 
                                       line.includes('Insights') || line.includes('Usage') ||
                                       (line.match(/^[A-Z]/) && line.split(' ').length <= 5));
                      
                      if (isHeader && !line.match(/^(No |Unknown|N\/A)/i)) {
                        // Start new section
                        if (currentSection && currentSection.items.length > 0) {
                          sections.push(currentSection);
                        }
                        currentSection = { title: line, items: [] };
                      } else if (currentSection) {
                        // Add to current section
                        if (line.includes(':')) {
                          const [key, ...valueParts] = line.split(':');
                          const value = valueParts.join(':').trim();
                          currentSection.items.push({ key: key.trim(), value: value || 'Not available' });
                        } else if (line.match(/^(No conversation found)/i)) {
                          // Special message
                          currentSection.items.push({ key: null, value: line, isNote: true });
                        } else if (line.length > 0) {
                          // Add as bullet point - these are items without colons
                          currentSection.items.push({ key: null, value: line, isBullet: true });
                        }
                      }
                    }
                    
                    // Add last section
                    if (currentSection && currentSection.items.length > 0) {
                      sections.push(currentSection);
                    }
                    
                    // For secondary users (sub-users), remove "Account Created" as it's not applicable
                    if (profileId) {
                      for (const section of sections) {
                        section.items = section.items.filter(item => item.key !== 'Account Created');
                      }
                    }

                    // Filter out empty sections
                    const validSections = sections.filter(s => s.items.length > 0);

                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Header Card */}
                        <Card
                          sx={{
                            background: "linear-gradient(135deg, #E1AD01 0%, #B8941F 100%)",
                            borderRadius: "12px",
                            boxShadow: "0 4px 12px rgba(204, 158, 73, 0.2)",
                          }}
                        >
                          <CardContent sx={{ padding: "24px !important" }}>
                            <Typography sx={{ color: "#000000", fontSize: "24px", fontWeight: 700, marginBottom: 1 }}>
                              {userName}
                            </Typography>
                            <Typography sx={{ color: "#333333", fontSize: "13px", fontFamily: "monospace" }}>
                              ID: {userId}
                            </Typography>
                          </CardContent>
                        </Card>

                        {/* Details Grid */}
                        <Grid container spacing={2}>
                          {validSections.map((section, idx) => (
                            <Grid item xs={12} md={6} key={idx}>
                              <Card
                                sx={{
                                  backgroundColor: "#FFFFFF",
                                  borderRadius: "12px",
                                  border: "1px solid #E0E0E0",
                                  height: '100%',
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                    transform: "translateY(-2px)",
                                  }
                                }}
                              >
                                <CardContent sx={{ padding: "20px !important" }}>
                                  <Typography 
                                    sx={{ 
                                      color: "#E1AD01", 
                                      fontSize: "18px", 
                                      fontWeight: 600, 
                                      marginBottom: 2,
                                      paddingBottom: 1.5,
                                      borderBottom: "2px solid #E1AD01"
                                    }}
                                  >
                                    {section.title}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {section.items.map((item, itemIdx) => {
                                      if (item.key) {
                                        // Key-value pair
                                        return (
                                          <Box key={itemIdx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                            <Typography sx={{ color: "#666666", fontSize: "14px", fontWeight: 500, flex: '0 0 auto', minWidth: '120px' }}>
                                              {item.key}:
                                            </Typography>
                                            <Typography sx={{ 
                                              color: item.value === 'Unknown' || item.value === 'Not available' ? "#999999" : "#333333", 
                                              fontSize: "14px", 
                                              fontWeight: 600,
                                              textAlign: 'right',
                                              flex: '1 1 auto',
                                              wordBreak: 'break-word',
                                              fontStyle: item.value === 'Unknown' || item.value === 'Not available' ? 'italic' : 'normal'
                                            }}>
                                              {item.value}
                                            </Typography>
                                          </Box>
                                        );
                                      } else if (item.isNote) {
                                        // Special note/message
                                        return (
                                          <Typography key={itemIdx} sx={{ 
                                            color: "#666666", 
                                            fontSize: "14px", 
                                            fontStyle: 'italic',
                                            padding: '8px 12px',
                                            backgroundColor: '#F9F9F9',
                                            borderRadius: '8px',
                                            borderLeft: '3px solid #E1AD01'
                                          }}>
                                            {item.value}
                                          </Typography>
                                        );
                                      } else if (item.isBullet) {
                                        // Bullet point
                                        return (
                                          <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <Box sx={{ 
                                              width: '6px', 
                                              height: '6px', 
                                              borderRadius: '50%', 
                                              backgroundColor: '#E1AD01',
                                              marginTop: '6px',
                                              flexShrink: 0
                                            }} />
                                            <Typography sx={{ 
                                              color: "#333333", 
                                              fontSize: "14px",
                                              lineHeight: 1.6,
                                              flex: 1
                                            }}>
                                              {item.value}
                                            </Typography>
                                          </Box>
                                        );
                                      }
                                      return null;
                                    })}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Charts Section */}
            <Accordion
              expanded={expandedPanel === "charts"}
              onChange={handleAccordionChange("charts")}
              sx={{
                backgroundColor: "#1A1A1A",
                borderRadius: "16px !important",
                marginBottom: 2,
                border: "1px solid #333333",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: "#E1AD01" }} />}
                sx={{
                  padding: "16px 24px",
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Charts
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "0",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "0 0 16px 16px",
                }}
              >
                <Box>
                  {userData.charts && typeof userData.charts === "object" ? (
                    <Box>
                      {/* Chart Tabs */}
                      <Box
                        sx={{
                          borderBottom: "1px solid #E0E0E0",
                          backgroundColor: "#F9F9F9",
                          overflowX: "auto",
                          "&::-webkit-scrollbar": {
                            height: "6px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#E1AD01",
                            borderRadius: "3px",
                          },
                        }}
                      >
                        <Tabs
                          value={selectedChart}
                          onChange={handleChartChange}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            "& .MuiTab-root": {
                              color: "#666666",
                              fontSize: "13px",
                              fontWeight: 500,
                              minHeight: "48px",
                              textTransform: "none",
                            },
                            "& .Mui-selected": {
                              color: "#E1AD01 !important",
                              fontWeight: 600,
                            },
                            "& .MuiTabs-indicator": {
                              backgroundColor: "#E1AD01",
                              height: "3px",
                            },
                          }}
                        >
                          {/* Indu Lagna Chart Tab First */}
                          {userData.charts.indu_lagna_chart && (
                            <Tab
                              key="indu_lagna_chart"
                              value="indu_lagna_chart"
                              label={getChartName("indu_lagna_chart")}
                            />
                          )}
                          {/* Planet Positions Tab */}
                          {userData.planet_positions && (
                            <Tab
                              key="planet_positions"
                              value="planet_positions"
                              label={getChartName("planet_positions")}
                            />
                          )}
                          {/* Karakamsha Lagna Chart Tab */}
                          {userData.karakamsha_lagna_chart && (
                            <Tab
                              key="karakamsha_lagna_chart"
                              value="karakamsha_lagna_chart"
                              label={getChartName("karakamsha_lagna_chart")}
                            />
                          )}
                          {/* Arudha Lagna Chart Tab */}
                          {userData.arudha_lagna_chart && (
                            <Tab
                              key="arudha_lagna_chart"
                              value="arudha_lagna_chart"
                              label={getChartName("arudha_lagna_chart")}
                            />
                          )}
                          {/* Then D Charts in numerical order (d1, d2, d3, ...) */}
                          {Object.keys(userData.charts)
                            .filter(key => key !== "indu_lagna_chart")
                            .sort((a, b) => {
                              // Extract the number from the chart key (e.g., "d1" -> 1)
                              const numA = parseInt(a.replace('d', ''));
                              const numB = parseInt(b.replace('d', ''));
                              return numA - numB;
                            })
                            .map((chartKey) => (
                              <Tab
                                key={chartKey}
                                value={chartKey}
                                label={getChartName(chartKey)}
                              />
                            ))}
                        </Tabs>
                      </Box>

                      {/* Chart Content */}
                      <Box sx={{ padding: "24px" }}>
                        {renderChartData(userData.charts)}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ padding: "24px" }}>
                      <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                        No chart data available.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Chat History Dialog */}
            <Dialog
              open={chatModalOpen}
              onClose={handleCloseChat}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  backgroundColor: '#1A1A1A',
                  borderRadius: '16px',
                  border: '1px solid #333333',
                }
              }}
              BackdropProps={{
                sx: {
                  backdropFilter: 'none',
                  backgroundColor: 'rgba(0,0,0,0.5)'
                }
              }}
            >
              <DialogTitle sx={{ 
                color: '#FFFFFF', 
                fontSize: '20px', 
                fontWeight: 600,
                borderBottom: '1px solid #333333'
              }}>
                Chat History
              </DialogTitle>
              <DialogContent sx={{
                backgroundColor: '#1A1A1A',
                color: '#FFFFFF',
                p: 0,
              }}>
                {chatModalLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: '#E1AD01' }} />
                  </Box>
                ) : chatModalError ? (
                  <Typography sx={{ color: '#ff6b6b', fontSize: '14px', p: 3 }}>{chatModalError}</Typography>
                ) : chatModalData ? (
                  (() => {
                    const messages = Array.isArray(chatModalData)
                      ? chatModalData
                      : Array.isArray(chatModalData.messages)
                        ? chatModalData.messages
                        : [];

                    if (messages.length === 0) {
                      return <Typography sx={{ color: '#999999', fontSize: '14px', p: 3 }}>No messages found.</Typography>;
                    }

                    return (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        px: 3,
                        py: 2,
                      }}>
                        {messages.map((m, idx) => {
                          const isUser = (m.role || m.sender || '').toLowerCase() === 'user';
                          const messageText = m.text || m.message || '';
                          const time = m.created_at?.$date
                            ? new Date(m.created_at.$date).toLocaleString('en-US', {
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                              })
                            : '';

                          return (
                            <Box
                              key={m._id?.$oid || idx}
                              sx={{
                                display: 'flex',
                                justifyContent: isUser ? 'flex-end' : 'flex-start',
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: '80%',
                                  backgroundColor: isUser ? '#E1AD01' : '#2A2A2A',
                                  color: isUser ? '#000000' : '#EAEAEA',
                                  borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                  px: 2.5,
                                  py: 1.5,
                                  border: isUser ? 'none' : '1px solid #3A3A3A',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: isUser ? 'rgba(0,0,0,0.6)' : '#E1AD01',
                                    mb: 0.5,
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {isUser ? 'User' : 'Astroma AI'}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    '& strong, & b': { fontWeight: 700 },
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: messageText
                                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                      .replace(/^## (.*?)$/gm, '<strong style="font-size:16px;display:block;margin-bottom:8px;">$1</strong>')
                                      .replace(/^\* {3}(.*?)$/gm, '<span style="display:block;padding-left:16px;margin:2px 0;">• $1</span>')
                                      .replace(/\n/g, '<br/>')
                                  }}
                                />
                                {time && (
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      color: isUser ? 'rgba(0,0,0,0.45)' : '#666666',
                                      mt: 1,
                                      textAlign: 'right',
                                    }}
                                  >
                                    {time}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()
                ) : (
                  <Typography sx={{ color: '#999999', fontSize: '14px', p: 3 }}>No chat data available.</Typography>
                )}
              </DialogContent>
              <DialogActions sx={{ 
                backgroundColor: '#1A1A1A', 
                borderTop: '1px solid #333333',
                padding: '16px 24px'
              }}>
                <Button 
                  onClick={handleCloseChat}
                  sx={{
                    color: '#E1AD01',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    '&:hover': {
                      backgroundColor: 'rgba(204, 158, 73, 0.08)'
                    }
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* Chats Section */}
            <Accordion
              expanded={expandedPanel === "chats"}
              onChange={handleAccordionChange("chats")}
              sx={{
                backgroundColor: "#1A1A1A",
                borderRadius: "16px !important",
                marginBottom: 2,
                border: "1px solid #333333",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: "#E1AD01" }} />}
                sx={{
                  padding: "16px 24px",
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Chats
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "24px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "0 0 16px 16px",
                }}
              >
                <Box sx={{ marginBottom: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search conversations..."
                    value={chatSearch}
                    onChange={(e) => setChatSearch(e.target.value)}
                    aria-label="Search conversations"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "6px",
                      color: "#333333",
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#E1AD01',
                        },
                        '&:hover fieldset': {
                          borderColor: '#E1AD01',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#E1AD01',
                          boxShadow: '0 0 0 2px rgba(204,158,73,0.12)',
                        },
                      },
                      // force input text color so typed value is visible
                      '& .MuiOutlinedInput-input': {
                        color: '#333333',
                      },
                      '& .MuiInputBase-input': {
                        color: '#333333',
                      },
                      // make placeholder clearly visible
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: '#8a8a8a',
                        opacity: 1,
                      },
                    }}
                  />
                </Box>

                {filteredConversations && filteredConversations.length > 0 ? (
                  filteredConversations.map((c) => (
                    <Box
                      key={c._id}
                      sx={{
                        marginBottom: 1,
                        cursor: 'pointer',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #eee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#FFF8E1',
                          borderColor: '#CC9E49',
                          boxShadow: '0 2px 8px rgba(204,158,73,0.15)',
                        },
                      }}
                      onClick={() => handleOpenChat(c._id)}
                    >
                      <Box>
                        <Typography sx={{ color: "#333333", fontWeight: 600, fontSize: "14px" }}>
                          {c.title}
                        </Typography>
                        <Typography sx={{ color: "#888", fontSize: "12px", marginTop: '2px' }}>
                          {new Date(c.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <ChevronRight sx={{ color: '#CC9E49', fontSize: 22 }} />
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                    No conversations found.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Reports Section */}
            <Accordion
              expanded={expandedPanel === "reports"}
              onChange={handleAccordionChange("reports")}
              sx={{
                backgroundColor: "#1A1A1A",
                borderRadius: "16px !important",
                marginBottom: 2,
                border: "1px solid #333333",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: "#E1AD01" }} />}
                sx={{
                  padding: "16px 24px",
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Reports
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "24px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "0 0 16px 16px",
                }}
              >
                {/* Personal Reports */}
                {userData.reports && userData.reports.length > 0 && (
                  <>
                    <Typography sx={{ color: "#333", fontWeight: 600, fontSize: "16px", marginBottom: 1.5 }}>
                      Personal Reports
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 3 }}>
                      {userData.reports.map((report) => (
                        <Card
                          key={report._id}
                          sx={{
                            backgroundColor: "#F9F9F9",
                            borderRadius: "12px",
                            border: "1px solid #E0E0E0",
                            boxShadow: "none",
                          }}
                        >
                          <CardContent sx={{ padding: "20px !important" }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: "#333333", fontWeight: 600, fontSize: "18px", marginBottom: 0.5 }}>
                                  {report.report_name || "Untitled Report"}
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                onClick={() => handleOpenReport(report.file_url)}
                                sx={{
                                  backgroundColor: "#E1AD01",
                                  color: "#000000",
                                  borderRadius: "20px",
                                  padding: "8px 20px",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  minWidth: "120px",
                                  "&:hover": { backgroundColor: "#B8941F" },
                                }}
                              >
                                View Report
                              </Button>
                            </Box>
                            {report.report_text && (
                              <Typography
                                sx={{
                                  color: "#666666",
                                  fontSize: "14px",
                                  marginTop: 1,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {report.report_text.substring(0, 200)}...
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </>
                )}

                {/* Compatibility Reports (is_comparison: false) */}
                {userData.compatibility_reports && userData.compatibility_reports.filter(r => !r.is_comparison).length > 0 && (
                  <>
                    <Typography sx={{ color: "#333", fontWeight: 600, fontSize: "16px", marginBottom: 1.5 }}>
                      Compatibility Reports
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 3 }}>
                      {userData.compatibility_reports.filter(r => !r.is_comparison).map((report) => (
                        <Card
                          key={report._id}
                          sx={{
                            backgroundColor: "#F9F9F9",
                            borderRadius: "12px",
                            border: "1px solid #E0E0E0",
                            boxShadow: "none",
                          }}
                        >
                          <CardContent sx={{ padding: "20px !important" }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: "#333333", fontWeight: 600, fontSize: "18px", marginBottom: 0.5 }}>
                                  {report.report_name || "Compatibility Report"}
                                </Typography>
                              </Box>
                              {report.pdf_report && (
                                <Button
                                  variant="contained"
                                  onClick={() => handleOpenReport(report.pdf_report)}
                                  sx={{
                                    backgroundColor: "#E1AD01",
                                    color: "#000000",
                                    borderRadius: "20px",
                                    padding: "8px 20px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    minWidth: "120px",
                                    "&:hover": { backgroundColor: "#B8941F" },
                                  }}
                                >
                                  View Report
                                </Button>
                              )}
                            </Box>
                            {report.report_text && (
                              <Typography
                                sx={{
                                  color: "#666666",
                                  fontSize: "14px",
                                  marginTop: 1,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {report.report_text.substring(0, 200)}...
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </>
                )}

                {/* Compare Reports (is_comparison: true) */}
                {userData.compatibility_reports && userData.compatibility_reports.filter(r => r.is_comparison).length > 0 && (
                  <>
                    <Typography sx={{ color: "#333", fontWeight: 600, fontSize: "16px", marginBottom: 1.5 }}>
                      Compare Reports
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {userData.compatibility_reports.filter(r => r.is_comparison).map((report) => (
                        <Card
                          key={report._id}
                          sx={{
                            backgroundColor: "#F9F9F9",
                            borderRadius: "12px",
                            border: "1px solid #E0E0E0",
                            boxShadow: "none",
                          }}
                        >
                          <CardContent sx={{ padding: "20px !important" }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: "#333333", fontWeight: 600, fontSize: "18px", marginBottom: 0.5 }}>
                                  {report.report_name || "Compare Report"}
                                </Typography>
                              </Box>
                              {report.pdf_report && (
                                <Button
                                  variant="contained"
                                  onClick={() => handleOpenReport(report.pdf_report)}
                                  sx={{
                                    backgroundColor: "#E1AD01",
                                    color: "#000000",
                                    borderRadius: "20px",
                                    padding: "8px 20px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    minWidth: "120px",
                                    "&:hover": { backgroundColor: "#B8941F" },
                                  }}
                                >
                                  View Report
                                </Button>
                              )}
                            </Box>
                            {report.report_text && (
                              <Typography
                                sx={{
                                  color: "#666666",
                                  fontSize: "14px",
                                  marginTop: 1,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {report.report_text.substring(0, 200)}...
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </>
                )}

                {(!userData.reports || userData.reports.length === 0) &&
                 (!userData.compatibility_reports || userData.compatibility_reports.length === 0) && (
                  <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                    No reports available.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Likes & Share Section */}
            <Accordion
              expanded={expandedPanel === "likes"}
              onChange={handleAccordionChange("likes")}
              sx={{
                backgroundColor: "#1A1A1A",
                borderRadius: "16px !important",
                marginBottom: 2,
                border: "1px solid #333333",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: "#E1AD01" }} />}
                sx={{
                  padding: "16px 24px",
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Likes & Dislike
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "24px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "0 0 16px 16px",
                }}
              >
                <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                  User engagement metrics including likes and shares will be
                  displayed here.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>
    </Box>
  );
};

export default UserDetails;
